/* eslint-disable no-param-reassign */
/* eslint-disable object-curly-newline */

import { Component, registerComponent } from 'js/component.js';
import { isEditModeFooter, setDataAttr, uuid, getViewportSize } from 'js/helpers.js';
import { scrollToAnchor } from 'js/ra.js';

const componentName = 'product-family-list';
const productDetailServletEndpoint = '/bin/rockwell-automation/product-details';
const addToBomEndpoint = 'https://www.rockwellautomation.com/my/bom?catalogNumber=${catalogNumber}';
const backupImage = 'https://www.rockwellautomation.com/product-images/${sku}/${sku}.jpg';
const productDetailPath = '/products/details.${catalogNumber}.html';

class ProductFamilyList extends Component {
    elements = {
        // Display
        container: this.element.querySelector(`.${componentName}__container`),
        productGrid: this.element.querySelector(`.${componentName}__grid--main`),
        subcategoryGrid: this.element.querySelector(`.${componentName}__grid--subcategory`),
        subcategoryView: this.element.querySelector(`.${componentName}__grid--subcategory-view`),
        productTemplate: this.element.querySelector(`.${componentName}__product-template`),
        loadingContainer: this.element.querySelector(`.${componentName}__loading-container`),
        compareItems: document.querySelectorAll(`.${componentName}__compare-modal-item`),
        compareCheckboxes: document.querySelectorAll(`.${componentName}__product-compare-checkbox`),
        popupChevrons: this.element.querySelectorAll(`.${componentName}__product-popup-chevron`),
        // Pagination
        paginationForm: this.element.querySelector(`.${componentName}__pagination-form`),
        resultsPerPageSelect: this.element.querySelector('select[name="results-per-page"]'),
        // Filtering
        // productSortSelect: this.element.querySelector('select[name="product-sort"]'),
        // productSortSelectStyler: this.element.querySelector(`.${componentName}__select-styler`),
        // productSortInputsMobile: this.element.querySelectorAll(`.${componentName}__filter-group-mobile-sort input`),
        filterContainer: this.element.querySelector(`.${componentName}__filter-container`),
        filterForm: this.element.querySelector(`.${componentName}__filter-form`),
        filterList: this.element.querySelector(`.${componentName}__filter-list`),
        filterGroupTemplate: this.element.querySelector(`.${componentName}__filter-group-template`),
        sliderTemplate: this.element.querySelector(`.${componentName}__filter-slider-container-template`),
        filterClearAll: this.element.querySelector(`.${componentName}__filter-clear-button`),
        filterCheckboxes: [],
        mobileFilterCloseButtons: this.element.querySelectorAll(`.${componentName}__filter-mobile-close`),
        mobileFilterOpenButton: this.element.querySelector(`.${componentName}__header-mobile-button`),
        textSearchInput: this.element.querySelector(`.${componentName}__filter-search`),
        chipContainer: this.element.querySelector(`.${componentName}__chip-container`),
    };

    state = {
        pageIndex: 0,
        totalPages: 0,
        totalResults: 0,
        resultsPerPage: 24,
        productList: [],
        sortBy: 'default',
        textSearch: '',
        filters: {},
        filterFacets: [],
        userCompareData: window.localStorage.getItem('userCompareData') ? window.localStorage.getItem('userCompareData').split(', ') : [],
    };

    constants = {
        app: '',
        defaultImage: '/etc.clientlibs/rockwell-aem-base/clientlibs/clientlib-base/resources/icons/image_unavailable.svg',
        ssDefaultImage: '/etc.clientlibs/rockwell-aem-base/clientlibs/clientlib-base/resources/icons/ss_image_unavailable.svg',
        productDataToFacetMap: {
            type: 'typeVals',
            brand: 'brandVals',
            productLifeCycleStatus: 'lifeCycleVals',
        },
        listType: 'family',
        categoryIds: '',
        maxFacets: 12,
        facetsExpandedOnLoad: 8,
    };

    // Helper method to get the appropriate default image based on app type
    getDefaultImage = () => {
        return this.constants.app?.toLowerCase() === 'ss' ? this.constants.ssDefaultImage : this.constants.defaultImage;
    };

    // Helper methods to conditionally show/hide spinner (custom loading for S+S pages)
    showSpinner = () => {
        if (this.constants.app?.toLowerCase() === 'ss') {
            // Show custom S+S loading state
            if (this.elements.loadingContainer) {
                this.elements.loadingContainer.style.display = 'flex';
                this.elements.productGrid.style.display = 'none';
            }
        } else {
            // Show RA spinner
            window.dispatchEvent(new CustomEvent('show-loading', null));
        }
    };

    hideSpinner = () => {
        if (this.constants.app?.toLowerCase() === 'ss') {
            // Hide custom S+S loading state
            if (this.elements.loadingContainer) {
                this.elements.loadingContainer.style.display = 'none';
                this.elements.productGrid.style.display = 'flex';
            }
        } else {
            // Hide RA spinner
            window.dispatchEvent(new CustomEvent('hide-loading', null));
        }
    };

    getProducts = async () => {
        const returnJson = {
            dataList: [],
            totalNumber: 0,
            facets: [],
        };
        try {
            let filterString = '';
            for (const [key, value] of Object.entries(this.state.filters)) {
                filterString += `${key}=${value.join('___')}---`;
            }

            const locale = document.documentElement.lang ? document.documentElement.lang.toLocaleLowerCase() : 'en-us';
            const countryCode = locale.split('-')[1];
            const response = await fetch(`${productDetailServletEndpoint}?countryCode=${countryCode}&app=${this.constants.app}&categoryIds=${this.constants.categoryIds}&startIndex=${(this.state.pageIndex * this.state.resultsPerPage)}&numResults=${this.state.resultsPerPage}&fields=all&sortBy=${this.state.sortBy}&elasticSearch=true&textSearch=${this.state.textSearch}${filterString === '' ? '' : `&filters=${filterString}`}`);
            const responseBuffer = await response.arrayBuffer();
            const responseString = new TextDecoder('UTF-8').decode(responseBuffer);
            const responseJson = JSON.parse(responseString);
            returnJson.dataList = responseJson.docs;
            returnJson.totalNumber = responseJson.total;
            returnJson.facets = responseJson.facets;
        } catch (error) {
            console.warn('Error:', error);
        }
        return returnJson;
    };

    addFacets = () => {
        this.state.filterFacets.forEach((facet, index) => {
            if (facet.type === 'checkbox') {
                const facetGroup = this.elements.filterGroupTemplate.cloneNode(true);
                if (index < this.constants.facetsExpandedOnLoad) {
                    facetGroup.setAttribute('open', true);
                }
                facetGroup.classList.remove(`${componentName}__filter-group-template`);
                facetGroup.setAttribute('data-facet-name', facet.name);
                facetGroup.querySelector(`.${componentName}__filter-summary`).innerHTML = this.toTitle(facet.name) + facetGroup.querySelector(`.${componentName}__filter-summary`).innerHTML;
                facetGroup.querySelector(`.${componentName}__filter-summary`).addEventListener('click', this.handleSummaryClick);

                facet.buckets.forEach((bucket, index) => {
                    const checkboxTemplate = facetGroup.querySelector(`.${componentName}__filter-checkbox-container-template`).cloneNode(true);
                    checkboxTemplate.classList.remove(`${componentName}__filter-checkbox-container-template`);
                    checkboxTemplate.setAttribute('data-bucket-name', encodeURIComponent(bucket.key));
                    
                    // Use original facet name for checkbox name if available (for Features group)
                    const checkboxName = bucket.facetName || this.constants.productDataToFacetMap[facet.name] || facet.name;
                    checkboxTemplate.querySelector(`.${componentName}__filter-checkbox`).setAttribute('name', checkboxName);
                    
                    // Use original key for value if available (for Features group)
                    const checkboxValue = bucket.originalKey !== undefined ? bucket.originalKey : bucket.key;
                    checkboxTemplate.querySelector(`.${componentName}__filter-checkbox`).setAttribute('value', encodeURIComponent(checkboxValue));
                    checkboxTemplate.setAttribute('data-original-index', index);
                    checkboxTemplate.querySelector(`.${componentName}__filter-checkbox`).setAttribute('name', this.constants.productDataToFacetMap[facet.name] || facet.name);
                    checkboxTemplate.querySelector(`.${componentName}__filter-checkbox`).setAttribute('value', encodeURIComponent(bucket.key));
                    checkboxTemplate.querySelector(`.${componentName}__filter-checkbox`).setAttribute('id', encodeURIComponent(facet.name) + '-' + encodeURIComponent(bucket.key));
                    checkboxTemplate.querySelector(`.${componentName}__filter-checkbox-count`).textContent = `(${bucket.count})`;
                    let checkboxLabel = bucket.key.replaceAll('&quot;', '"');
                    if (checkboxLabel.toLowerCase() === 'false') {
                        checkboxLabel = 'No';
                    }
                    if (checkboxLabel.toLowerCase() === 'true') {
                        checkboxLabel = 'Yes';
                    }
                    checkboxTemplate.querySelector('label').textContent = checkboxLabel;
                    checkboxTemplate.querySelector('label').setAttribute('for', encodeURIComponent(facet.name) + '-' + encodeURIComponent(bucket.key));
                    
                    // Add special icon for Build America, Buy America
                    if (bucket.key === 'Build America, Buy America') {
                        const iconElement = document.createElement('img');
                        iconElement.className = `${componentName}__filter-checkbox-icon`;
                        iconElement.src = 'https://dev-aem.rockwellautomation.com/content/dam/rockwell-automation/sites/images/logos/BABA%20logo%20flattened.svg';
                        iconElement.alt = 'BABA Logo';
                        iconElement.style.marginRight = '4px';
                        iconElement.style.width = '16px';
                        iconElement.style.height = '16px';
                        iconElement.style.verticalAlign = 'bottom';
                        checkboxTemplate.querySelector('label').insertBefore(iconElement, checkboxTemplate.querySelector('label').firstChild);
                    }
                    
                    facetGroup.querySelector(`.${componentName}__filter-content`).appendChild(checkboxTemplate);
                    this.elements.filterCheckboxes.push(checkboxTemplate.querySelector(`.${componentName}__filter-checkbox`));
                });
                
                this.elements.filterList.appendChild(facetGroup);

                facetGroup.querySelector(`.${componentName}__search-field-placeholder`).textContent = `Search ${this.toTitle(facet.name)}`;
                facetGroup.querySelector(`.${componentName}__filter-group-search`).setAttribute('name', this.constants.productDataToFacetMap[facet.name] || facet.name);
                if (facetGroup.querySelectorAll(`.${componentName}__filter-checkbox-container`).length > 10) {
                    facetGroup.querySelector(`.${componentName}__filter-search-container`).classList.remove('hidden');
                    facetGroup.querySelector(`.${componentName}__filter-group-search`).addEventListener('input', () => {
                        const searchValue = facetGroup.querySelector(`.${componentName}__filter-group-search`).value.toLowerCase();
                        facetGroup.querySelectorAll(`.${componentName}__filter-checkbox-container`).forEach((checkbox) => {
                            if (checkbox.querySelector('label').textContent.toLowerCase().includes(searchValue)) {
                                checkbox.classList.remove('hidden');
                            } else {
                                checkbox.classList.add('hidden');
                            }
                        });
                    });
                }
            } else if (facet.type === 'slider') {
                // const sliderContainer = this.elements.sliderTemplate.cloneNode(true);
                // sliderContainer.classList.remove(`${componentName}__filter-slider-container-template`);
                // sliderContainer.querySelector(`.${componentName}__filter-slider-label-text`).textContent = `${facet.name.replace('range-input-', '')}${facet.unit ? ` (${facet.unit})` : ''}`;
                // sliderContainer.querySelector(`.${componentName}__filter-slider-label-min`).textContent = facet.min;
                // sliderContainer.querySelector(`.${componentName}__filter-slider-label-max`).textContent = facet.max;
                // sliderContainer.querySelectorAll(`.${componentName}__filter-slider-range`).forEach((slider, index) => {
                //     slider.setAttribute('name', `${facet.name}-${index}`);
                //     slider.setAttribute('data-index', index);
                //     slider.setAttribute('min', facet.min);
                //     slider.setAttribute('max', facet.max);
                //     slider.setAttribute('step', (facet.max - facet.min) / 100);
                //     slider.setAttribute('value', index === 0 ? facet.min : facet.max);
                //     slider.setAttribute('aria-label', `${facet.name} ${index === 0 ? 'min' : 'max'}`);
                //     slider.addEventListener('change', (event) => {
                //         this.handleProductFilterChange();
                //     });
                //     slider.addEventListener('input', () => {
                //         const minInput = sliderContainer.querySelector(`.${componentName}__filter-slider-range[data-index="0"]`).valueAsNumber <= sliderContainer.querySelector(`.${componentName}__filter-slider-range[data-index="1"]`).valueAsNumber ? sliderContainer.querySelector(`.${componentName}__filter-slider-range[data-index="0"]`) : sliderContainer.querySelector(`.${componentName}__filter-slider-range[data-index="1"]`);
                //         const maxInput = sliderContainer.querySelector(`.${componentName}__filter-slider-range[data-index="0"]`).valueAsNumber > sliderContainer.querySelector(`.${componentName}__filter-slider-range[data-index="1"]`).valueAsNumber ? sliderContainer.querySelector(`.${componentName}__filter-slider-range[data-index="0"]`) : sliderContainer.querySelector(`.${componentName}__filter-slider-range[data-index="1"]`);
                //         this.fillSlider(minInput, maxInput, '#949494', '#003E7E', sliderContainer.querySelector(`.${componentName}__filter-slider-range[data-index="0"]`));
                //     });
                // });
                // this.elements.filterList.appendChild(sliderContainer);
            }
        });

        if (this.element.querySelector(`.${componentName}__filter-checkbox[name="bestSellers"]`)) {
            this.elements.filterCheckboxes.push(this.element.querySelector(`.${componentName}__filter-checkbox[name="bestSellers"]`));
        }
        if (this.element.querySelector(`.${componentName}__filter-checkbox[name="discontinued"]`)) {
            this.elements.filterCheckboxes.push(this.element.querySelector(`.${componentName}__filter-checkbox[name="discontinued"]`));
        }
    }

    initFacets = (facets) => {
        if (!facets) {
            return;
        }
        
        // Create a Features facet group for the new facets
        const featuresFacetObject = {
            name: 'Features',
            buckets: [],
            facetedProducts: 0,
            type: 'checkbox',
        };
        
        for (const [facetName, facetBuckets] of Object.entries(facets)) {
            
            if (facetName === 'classification_features' && this.constants.listType != 'search') {
                this.initDynamicFacets(facetBuckets.buckets);
                continue;
            } else if (facetName === 'classification_numValues' && this.constants.listType != 'search') {
                // this.initNumericFacets(facetBuckets.buckets);
                continue;
            } else if (facetName === 'primary_category_filter' && this.constants.listType == 'search') {
                const facetObject = {
                    name: 'Product Category',
                    buckets: [],
                    facetedProducts: 0,
                    type: 'checkbox',
                };
                if (facetBuckets.buckets && typeof facetBuckets.buckets[Symbol.iterator] === 'function') {
                    for (const bucket of facetBuckets.buckets) {
                        if (bucket.count === 0) {
                            continue;
                        }
                        facetObject.buckets.push(bucket);
                        facetObject.facetedProducts += bucket.count;
                    }
                }
                if (facetObject.buckets.length > 1) {
                    this.state.filterFacets.push(facetObject);
                }
            } else if (facetName === 'salesOrg_repairable') {
                // Only add the REPAIRABLE bucket to Features
                if (facetBuckets.buckets && typeof facetBuckets.buckets[Symbol.iterator] === 'function') {
                    for (const bucket of facetBuckets.buckets) {
                        if ((bucket.key === 'REPAIRABLE') && bucket.count > 0) {
                            featuresFacetObject.buckets.push({
                                key: 'Repairable product',
                                count: bucket.count,
                                originalKey: bucket.key,
                                facetName: facetName
                            });
                            featuresFacetObject.facetedProducts += bucket.count;
                        }
                    }
                }
            } else if (facetName === 'registerable') {
                // Only add the "1" bucket to Features
                if (facetBuckets.buckets && typeof facetBuckets.buckets[Symbol.iterator] === 'function') {
                    for (const bucket of facetBuckets.buckets) {
                        if ((bucket.key === 1) && bucket.count > 0) {
                            featuresFacetObject.buckets.push({
                                key: 'Product Registration',
                                count: bucket.count,
                                originalKey: bucket.key,
                                facetName: facetName
                            });
                            featuresFacetObject.facetedProducts += bucket.count;
                        }
                    }
                }
            } else if (facetName === 'pim_baba') {
                // Only add the PIM_BABA bucket to Features
                if (facetBuckets.buckets && typeof facetBuckets.buckets[Symbol.iterator] === 'function') {
                    for (const bucket of facetBuckets.buckets) {
                        if ((bucket.key === 'PIM_BABA') && bucket.count > 0) {
                            featuresFacetObject.buckets.push({
                                key: 'Build America, Buy America',
                                count: bucket.count,
                                originalKey: bucket.key,
                                facetName: facetName
                            });
                            featuresFacetObject.facetedProducts += bucket.count;
                        }
                    }
                }
            }
        }
        
        // Add the Features facet group if it has buckets
        if (featuresFacetObject.buckets.length > 0) {
            this.state.filterFacets.push(featuresFacetObject);
        }
        
        this.state.filterFacets.sort((a, b) => {
            // Prioritize Features group
            if (a.name === 'Features' && b.name !== 'Features') {
                return -1;
            }
            if (b.name === 'Features' && a.name !== 'Features') {
                return 1;
            }
            
            // Then sort by bucket count, faceted products, and name
            if (a.buckets.length !== b.buckets.length) {
                return b.buckets.length - a.buckets.length;
            } else if (a.facetedProducts !== b.facetedProducts) {
                return b.facetedProducts - a.facetedProducts;
            } else {
                return a.name.localeCompare(b.name);
            }
        });
        
        this.state.filterFacets = this.state.filterFacets.slice(0, this.constants.maxFacets);
    };

    initDynamicFacets = (facets) => {
        for (const facet of facets) {
            if (facet.unique_values.buckets && typeof facet.unique_values.buckets[Symbol.iterator] === 'function') {
                const facetObject = {
                    name: facet.key,
                    buckets: [],
                    facetedProducts: 0,
                    type: 'checkbox',
                };
                for (const bucket of facet.unique_values.buckets) {
                    if (bucket.doc_count === 0) {
                        continue;
                    }
                    facetObject.buckets.push({
                        key: bucket.key,
                        count: bucket.doc_count,
                    });
                    facetObject.facetedProducts += bucket.doc_count;
                }
                if (facetObject.buckets.length > 1) {
                    this.state.filterFacets.push(facetObject);
                }
            }
        }
    };

    // initNumericFacets = (facets) => {
    //     if (!facets) {
    //         return;
    //     }
    //     for (const facet of facets) {
    //         if (facet.unique_values.buckets && typeof facet.unique_values.buckets[Symbol.iterator] === 'function') {
    //             const facetObject = {
    //                 name: `range-input-${facet.key}`,
    //                 buckets: [],
    //                 facetedProducts: 0,
    //                 type: 'slider',
    //                 min: Number.MAX_SAFE_INTEGER,
    //                 max: Number.MIN_SAFE_INTEGER,
    //                 unit: '',
    //             };
    //             for (const bucket of facet.unique_values.buckets) {
    //                 if (bucket.filtered_docs.doc_count === 0) {
    //                     continue;
    //                 }
    //                 if (bucket.filtered_docs.unit_symbols.buckets.length > 1 || (facet.unit !== '' && bucket.filtered_docs.unit_symbols.buckets.length === 1 && bucket.filtered_docs.unit_symbols.buckets[0].key !== facet.unit)) {
    //                     facetObject.unit = null;
    //                     break;
    //                 } else if (bucket.filtered_docs.unit_symbols.buckets.length === 1) {
    //                     facetObject.unit = bucket.filtered_docs.unit_symbols.buckets[0].key;
    //                 }
    //                 facetObject.buckets.push({
    //                     key: bucket.key,
    //                     count: bucket.doc_count,
    //                 });
    //                 facetObject.min = Math.min(facetObject.min, bucket.key);
    //                 facetObject.max = Math.max(facetObject.max, bucket.key);
    //                 facetObject.facetedProducts += bucket.doc_count;
    //             }
    //             if (facetObject.buckets.length > 1 && facetObject.min !== Number.MAX_SAFE_INTEGER && facetObject.max !== Number.MIN_SAFE_INTEGER && facetObject.min !== facetObject.max && facetObject.unit !== null) {
    //                 this.state.filterFacets.push(facetObject);
    //             }
    //         }
    //     }
    // };

    // fillSlider = (from, to, sliderColor, rangeColor, controlSlider) => {
    //     const rangeDistance = to.max-to.min;
    //     const fromPosition = from.value - to.min;
    //     const toPosition = to.value - to.min;
    //     controlSlider.style.setProperty('--slider-fill-gradient', `linear-gradient(
    //       to right,
    //       ${sliderColor} 0%,
    //       ${sliderColor} ${(fromPosition)/(rangeDistance)*100}%,
    //       ${rangeColor} ${((fromPosition)/(rangeDistance))*100}%,
    //       ${rangeColor} ${(toPosition)/(rangeDistance)*100}%,
    //       ${sliderColor} ${(toPosition)/(rangeDistance)*100}%,
    //       ${sliderColor} 100%)`);
    // };

    updateFacetCounts = (facets) => {
        if (!facets) {
            return;
        }
        // Disable all checkboxes by default except those in a group with a checked checkbox
        this.elements.filterList.querySelectorAll(`.${componentName}__filter-checkbox-container`).forEach((checkbox) => {
            if (checkbox.closest(`.${componentName}__filter-content`) && !checkbox.closest(`.${componentName}__filter-content`).querySelector(`.${componentName}__filter-checkbox:checked`) && checkbox.querySelector(`.${componentName}__filter-checkbox`)) {
                checkbox.querySelector(`.${componentName}__filter-checkbox`).disabled = !checkbox.querySelector(`.${componentName}__filter-checkbox`).checked;
                if (checkbox.querySelector(`.${componentName}__filter-checkbox-count`)) {
                    checkbox.querySelector(`.${componentName}__filter-checkbox-count`).textContent = '(0)';
                }
            }
        });

        for (let [facetName, facetBuckets] of Object.entries(facets)) {
            if (facetName === 'classification_features') {
                this.updateDynamicFacetCounts(facetBuckets.buckets);
                continue;
            } else if (facetName === 'primary_category_filter') {
                facetName = 'Product Category';
            }
            
            // Handle Features facet group specially
            if (facetName === 'salesOrg_repairable' || facetName === 'registerable' || facetName === 'pim_baba') {
                const featuresFacetGroup = this.elements.filterList.querySelector(`.${componentName}__filter-group[data-facet-name="Features"]`);
                if (featuresFacetGroup && facetBuckets.buckets && typeof facetBuckets.buckets[Symbol.iterator] === 'function') {
                    for (const bucket of facetBuckets.buckets) {
                        let displayKey = '';
                        if (facetName === 'salesOrg_repairable' && bucket.key === 'REPAIRABLE') {
                            displayKey = 'Repairable product';
                        } else if (facetName === 'registerable' && bucket.key === 1) {
                            displayKey = 'Product Registration';
                        } else if (facetName === 'pim_baba' && bucket.key === 'PIM_BABA') {
                            displayKey = 'Build America, Buy America';
                        }
                        
                        if (displayKey) {
                            const bucketCheckbox = featuresFacetGroup.querySelector(`.${componentName}__filter-checkbox-container[data-bucket-name="${encodeURIComponent(displayKey)}"]`);
                            if (bucketCheckbox) {
                                bucketCheckbox.querySelector(`.${componentName}__filter-checkbox-count`).textContent = `(${bucket.count})`;
                                bucketCheckbox.querySelector(`.${componentName}__filter-checkbox`).disabled = bucket.count === 0;
                            }
                        }
                    }
                }
                continue;
            }
            
            const facetGroup = this.elements.filterList.querySelector(`.${componentName}__filter-group[data-facet-name="${facetName}"]`);
            if (facetGroup && facetBuckets.buckets && typeof facetBuckets.buckets[Symbol.iterator] === 'function') {
                for (const bucket of facetBuckets.buckets) {
                    const bucketCheckbox = facetGroup.querySelector(`.${componentName}__filter-checkbox-container[data-bucket-name="${encodeURIComponent(bucket.key)}"]`);
                    if (bucketCheckbox) {
                        bucketCheckbox.querySelector(`.${componentName}__filter-checkbox-count`).textContent = `(${bucket.count})`;
                        bucketCheckbox.querySelector(`.${componentName}__filter-checkbox`).disabled = bucket.count === 0;
                    }
                }
            }
        }
    };

    updateDynamicFacetCounts = (facets) => {
        for (const facet of facets) {
            const facetGroup = this.elements.filterList.querySelector(`.${componentName}__filter-group[data-facet-name="${facet.key}"]`);
            if (facetGroup && facet.unique_values.buckets && typeof facet.unique_values.buckets[Symbol.iterator] === 'function') {
                for (const bucket of facet.unique_values.buckets) {
                    const bucketCheckbox = facetGroup.querySelector(`.${componentName}__filter-checkbox-container[data-bucket-name="${encodeURIComponent(bucket.key)}"]`);
                    if (bucketCheckbox) {
                        bucketCheckbox.querySelector(`.${componentName}__filter-checkbox-count`).textContent = `(${bucket.doc_count})`;
                        bucketCheckbox.querySelector(`.${componentName}__filter-checkbox`).disabled = bucket.doc_count === 0;
                    }
                }
            }
        }
    };

    createProductElement = (product) => {
        const productElement = this.elements.productTemplate.cloneNode(true);
        productElement.classList.remove(`${componentName}__product-template`);
        productElement.setAttribute('data-product-id', product.catalogNumber);
        if (!product.salesOrg_preferredAvailability) {
            productElement.querySelector(`.${componentName}__product-best-selling-tag`).remove();
        }
        productElement.querySelector(`.${componentName}__product-image`).addEventListener('error', () => {
            if (productElement.querySelector(`.${componentName}__product-image`).getAttribute('src') === backupImage.replaceAll('${sku}', product.catalogNumber)) {
                productElement.querySelector(`.${componentName}__product-image`).setAttribute('src', this.getDefaultImage());
            } else if (productElement.querySelector(`.${componentName}__product-image`).getAttribute('src') === this.getDefaultImage()) {
                return;
            } else {
                productElement.querySelector(`.${componentName}__product-image`).setAttribute('src', backupImage.replaceAll('${sku}', product.catalogNumber));
            }
        });
        productElement.querySelector(`.${componentName}__product-image`).setAttribute('src', (product.picture_url ? product.picture_url : this.getDefaultImage()));
        productElement.querySelector(`.${componentName}__product-image`).setAttribute('alt', `${product.catalogNumber} ${product.names_value} product image`);
        const locale = document.documentElement.lang ? document.documentElement.lang.toLocaleLowerCase() : 'en-us';
        const productLink = `${window.location.origin}${this.constants.app.toLowerCase() !== 'ss' && !window.location.origin.endsWith('.com.cn') ? `/${locale}` : ''}${productDetailPath.replace('${catalogNumber}', this.encodeCatalogNumber(product.catalogNumber))}`;
        productElement.querySelectorAll(`.${componentName}__product-link`).forEach((link) => {
            link.setAttribute('href', productLink);
        });
        this.addTextContentOrRemove(productElement.querySelector(`.${componentName}__product-title`), product.names_value);
        this.addTextContentOrRemove(productElement.querySelector(`.${componentName}__product-catalog-number`), product.catalogNumber);
        this.addTextContentOrRemove(productElement.querySelector(`.${componentName}__product-lifecycle-status-text`), product.productLifeCycleStatus?.toLowerCase().replaceAll('_', ' '));
        productElement.querySelector(`.${componentName}__product-lifecycle-status-dot`)?.classList.add(`${componentName}__product-lifecycle-status-dot--${product.productLifeCycleStatus ? product.productLifeCycleStatus.toLowerCase() : 'hidden'}`);
        this.addTextContentOrRemove(productElement.querySelector(`.${componentName}__product-description`), product.R_description ? product.R_description : product.technicalDescription_value);
        this.addTextContentOrRemove(productElement.querySelector(`.${componentName}__product-type`), "Product");
        productElement.querySelector(`.${componentName}__product-bom-link`).setAttribute('href', addToBomEndpoint.replace('${catalogNumber}', product.catalogNumber));
        if (product.productConfigLink) {
            productElement.querySelector(`.${componentName}__product-configure-link`).setAttribute('href', product.productConfigLink);
        } else {
            productElement.querySelector(`.${componentName}__product-configure-link`).remove();
        }

        const compareCheckbox = productElement.querySelector(`.${componentName}__product-compare-checkbox`);
        compareCheckbox.setAttribute('id', `${product.catalogNumber}-compare`);
        productElement.querySelector(`.${componentName}__product-compare-label`).setAttribute('for', `${product.catalogNumber}-compare`);
        if (this.state.userCompareData.includes(product.catalogNumber)) {
            compareCheckbox.checked = true;
        }
        compareCheckbox.setAttribute('data-sku', product.catalogNumber);
        compareCheckbox.addEventListener('change', async (event) => {
            await this.compareCheckboxClick(event);
        });
        compareCheckbox.addEventListener('keydown', async (event) => {
            if (event.key === 'Enter') {
                await compareCheckbox.click();
            }
        });

        // productElement.querySelectorAll(`.${componentName}__product-list-price`).forEach((priceElement) => {
        //     this.addTextContentOrRemove(priceElement, this.numberWithCommas(product.currentPrice_value));
        // });
        // productElement.querySelectorAll(`.${componentName}__product-stock-status`).forEach((stockStatusElement) => {
        //     if (product.salesOrg_stockStatus === 'STOCKED') {
        //         if (product.salesOrg_leadTimeStatus && (product.salesOrg_leadTimeStatus == product.salesOrg_leadTime)) {
        //             stockStatusElement.textContent = `Stocked, Ships in ${product.salesOrg_leadTime} days`;
        //         } else {
        //             this.addTextContentOrRemove(stockStatusElement, product.salesOrg_leadTimeStatus);
        //         }
        //     } else if (product.salesOrg_stockStatus === 'NON_STOCKED') {
        //         this.addTextContentOrRemove(stockStatusElement, 'Not in Stock');
        //     }
        // });

        productElement.querySelector(`.${componentName}__product-popup-chevron`).addEventListener('click', () => {
            if (productElement.classList.contains('popup-active')) {
                productElement.classList.remove('popup-active');
            } else {
                productElement.classList.add('popup-active');
            }
        });
        return productElement;
    };

    addTextContentOrRemove = (element, textContent) => {
        if (textContent) {
            element.textContent += textContent;
        } else {
            element.remove();
        }
    };

    numberWithCommas = (x) => {
        if (x) {
            return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        }
        return '';
    };

    updatePageIndex = (pageIndex) => {
        this.state.pageIndex = pageIndex;
    };

    handlePaginationFormSubmit = async (event) => {
        event.preventDefault();
        this.showSpinner();
        this.elements.paginationForm.querySelector(`.${componentName}__pagination-button[name="prev"]`).setAttribute('disabled', true);
        this.elements.paginationForm.querySelector(`.${componentName}__pagination-button[name="next"]`).setAttribute('disabled', true);
        this.elements.paginationForm.querySelector(`.${componentName}__pagination-input`).setAttribute('disabled', true);
        this.elements.paginationForm.querySelector(`.${componentName}__pagination-input`).value = this.state.pageIndex + 1;
        if (this.constants.listType === 'category') {
            let index = 0;
            const products = this.elements.subcategoryGrid.querySelectorAll(`.${componentName}__product`);
            for (let i = 0; i < products.length; i++) {
                const product = products[i];
                if (product.classList.contains('filtered-out')) {
                    continue;
                }
                if ((index < (this.state.resultsPerPage * this.state.pageIndex)) || (index >= (this.state.resultsPerPage * (this.state.pageIndex + 1)))) {
                    product.classList.add('hidden');
                } else {
                    product.classList.remove('hidden');
                }
                index++;
            }
        } else {
            const formData = new FormData(event.target);
            const productResults = await this.getProducts();
            this.state.productList = productResults.dataList;
            this.elements.productGrid.querySelectorAll(`.${componentName}__product`).forEach((product) => {
                if (!product.classList.contains(`${componentName}__product-template`)) {
                    product.remove();
                }
            });
            this.state.productList.forEach((product) => {
                this.elements.productGrid.appendChild(this.createProductElement(product));
            });
        }
        [...this.element.querySelectorAll(`.${componentName}__pagination-start-index`)].forEach((startIndex) => {
            startIndex.textContent = (this.state.pageIndex * this.state.resultsPerPage) + 1;
        });
        [...this.element.querySelectorAll(`.${componentName}__pagination-end-index`)].forEach((endIndex) => {
            endIndex.textContent = Math.min((this.state.pageIndex + 1) * this.state.resultsPerPage, this.state.totalResults);
        });
        this.hideSpinner();
        if (this.state.pageIndex < this.state.totalPages - 1) {
            this.elements.paginationForm.querySelector(`.${componentName}__pagination-button[name="next"]`).removeAttribute('disabled');
        }
        if (this.state.pageIndex > 0) {
            this.elements.paginationForm.querySelector(`.${componentName}__pagination-button[name="prev"]`).removeAttribute('disabled');
        }
        this.elements.paginationForm.querySelector(`.${componentName}__pagination-input`).removeAttribute('disabled');
    };

    handleResultsPerPageChange = async (event) => {
        this.showSpinner();
        this.elements.paginationForm.querySelector(`.${componentName}__pagination-button[name="prev"]`).setAttribute('disabled', true);
        this.elements.paginationForm.querySelector(`.${componentName}__pagination-button[name="next"]`).setAttribute('disabled', true);
        this.elements.paginationForm.querySelector(`.${componentName}__pagination-input`).setAttribute('disabled', true);
        this.state.resultsPerPage = parseInt(event.target.value, 10);
        this.state.pageIndex = 0;
        this.state.totalPages = Math.ceil(this.state.totalResults / this.state.resultsPerPage);
        if (this.constants.listType === 'category') {
            let index = 0;
            const products = this.elements.subcategoryGrid.querySelectorAll(`.${componentName}__product`);
            for (let i = 0; i < products.length; i++) {
                const product = products[i];
                if (product.classList.contains('filtered-out')) {
                    continue;
                }
                if ((index < (this.state.resultsPerPage * this.state.pageIndex)) || (index >= (this.state.resultsPerPage * (this.state.pageIndex + 1)))) {
                    product.classList.add('hidden');
                } else {
                    product.classList.remove('hidden');
                }
                index++;
            }
        } else {
            const productResults = await this.getProducts();
            this.state.productList = productResults.dataList;
            this.elements.productGrid.querySelectorAll(`.${componentName}__product`).forEach((product) => {
                if (!product.classList.contains(`${componentName}__product-template`)) {
                    product.remove();
                }
            });
            this.state.productList.forEach((product) => {
                this.elements.productGrid.appendChild(this.createProductElement(product));
            });
        }
        this.elements.paginationForm.querySelector(`.${componentName}__pagination-input`).value = 1;
        [...this.element.querySelectorAll(`.${componentName}__pagination-start-index`)].forEach((startIndex) => {
            startIndex.textContent = 1;
        });
        [...this.element.querySelectorAll(`.${componentName}__pagination-end-index`)].forEach((endIndex) => {
            endIndex.textContent = Math.min(this.state.resultsPerPage, this.state.totalResults);
        });
        this.elements.paginationForm.querySelector('span[data-name="page-count"]').textContent = this.state.totalPages;
        this.elements.paginationForm.querySelector(`.${componentName}__pagination-input`).max = this.state.totalPages;
        this.hideSpinner();
        if (this.state.pageIndex < this.state.totalPages - 1) {
            this.elements.paginationForm.querySelector(`.${componentName}__pagination-button[name="next"]`).removeAttribute('disabled');
        }
        this.elements.paginationForm.querySelector(`.${componentName}__pagination-input`).removeAttribute('disabled');
        this.elements.paginationForm.querySelector(`.${componentName}__pagination-input`).setAttribute('max', this.state.totalPages);
    };

    handleProductSortChange = async (sortValue) => {
        this.showSpinner();
        this.elements.paginationForm.querySelector(`.${componentName}__pagination-button[name="prev"]`).setAttribute('disabled', true);
        this.elements.paginationForm.querySelector(`.${componentName}__pagination-button[name="next"]`).setAttribute('disabled', true);
        this.elements.paginationForm.querySelector(`.${componentName}__pagination-input`).setAttribute('disabled', true);
        this.state.pageIndex = 0;
        this.state.totalPages = Math.ceil(this.state.totalResults / this.state.resultsPerPage);
        this.state.sortBy = sortValue;
        if (this.constants.listType === 'category') {
            let sortedProductElements = [];
            if (this.state.sortBy === 'alphaSort--asc') {
                sortedProductElements = [...this.elements.subcategoryGrid.querySelectorAll(`.${componentName}__product`)].sort(this.sortProductElementsByTitle);
            } else if (this.state.sortBy === 'alphaSort--desc') {
                sortedProductElements = [...this.elements.subcategoryGrid.querySelectorAll(`.${componentName}__product`)].sort(this.sortProductElementsByTitle).reverse();
            }
            this.elements.subcategoryGrid.innerHTML = '';
            sortedProductElements.forEach((product) => {
                this.elements.subcategoryGrid.appendChild(product);
            });
            let index = 0;
            for (let i = 0; i < sortedProductElements.length; i++) {
                const product = sortedProductElements[i];
                if (product.classList.contains('filtered-out')) {
                    continue;
                }
                if (index >= this.state.resultsPerPage) {
                    product.classList.add('hidden');
                } else {
                    product.classList.remove('hidden');
                }
                index++;
            }
        } else {
            const productResults = await this.getProducts();
            this.state.productList = productResults.dataList;
            this.elements.productGrid.querySelectorAll(`.${componentName}__product`).forEach((product) => {
                if (!product.classList.contains(`${componentName}__product-template`)) {
                    product.remove();
                }
            });
            this.state.productList.forEach((product) => {
                this.elements.productGrid.appendChild(this.createProductElement(product));
            });
        }
        this.elements.paginationForm.querySelector(`.${componentName}__pagination-input`).value = 1;
        [...this.element.querySelectorAll(`.${componentName}__pagination-start-index`)].forEach((startIndex) => {
            startIndex.textContent = 1;
        });
        [...this.element.querySelectorAll(`.${componentName}__pagination-end-index`)].forEach((endIndex) => {
            endIndex.textContent = Math.min(this.state.resultsPerPage, this.state.totalResults);
        });
        this.elements.paginationForm.querySelector('span[data-name="page-count"]').textContent = this.state.totalPages;
        this.hideSpinner();
        if (this.state.pageIndex < this.state.totalPages - 1) {
            this.elements.paginationForm.querySelector(`.${componentName}__pagination-button[name="next"]`).removeAttribute('disabled');
        }
        this.elements.paginationForm.querySelector(`.${componentName}__pagination-input`).removeAttribute('disabled');
        this.elements.paginationForm.querySelector(`.${componentName}__pagination-input`).setAttribute('max', this.state.totalPages);
    };

    sortProductElementsByTitle = (productA, productB) => {
        const productAName = productA.querySelector(`.${componentName}__product-title`).textContent.toLowerCase();
        const productBName = productB.querySelector(`.${componentName}__product-title`).textContent.toLowerCase();
        if (productAName < productBName) {
            return -1;
        }
        if (productAName > productBName) {
            return 1;
        }
        return 0;
    };

    handleTextSearchChange = async () => {
        this.showSpinner();
        this.elements.paginationForm.querySelector(`.${componentName}__pagination-button[name="prev"]`).setAttribute('disabled', true);
        this.elements.paginationForm.querySelector(`.${componentName}__pagination-button[name="next"]`).setAttribute('disabled', true);
        this.elements.paginationForm.querySelector(`.${componentName}__pagination-input`).setAttribute('disabled', true);
        this.state.pageIndex = 0;
        this.state.totalPages = Math.ceil(this.state.totalResults / this.state.resultsPerPage);
        this.elements.filterClearAll.hidden = (this.elements.filterForm.querySelectorAll('input[type="checkbox"]:checked').length === 0 && (this.state.textSearch === ''));

        if (this.constants.listType === 'category') {
            const filteredProductElements = [...this.elements.subcategoryGrid.querySelectorAll(`.${componentName}__product`)].filter((product) => {
                return (product.querySelector(`.${componentName}__product-title`).textContent.toLowerCase().includes(this.state.textSearch.toLowerCase()) || (product.querySelector(`.${componentName}__product-description`) && product.querySelector(`.${componentName}__product-description`).textContent.toLowerCase().includes(this.state.textSearch.toLowerCase())));
            });
            const products = this.elements.subcategoryGrid.querySelectorAll(`.${componentName}__product`);
            let index = 0;
            for (let i = 0; i < products.length; i++) {
                const product = products[i];
                product.classList.add('filtered-out');
                if (filteredProductElements.includes(product)) {
                    product.classList.remove('filtered-out');
                } else {
                    continue;
                }
                if (index >= this.state.resultsPerPage) {
                    product.classList.add('hidden');
                }
                index++;
            }
            this.state.totalResults = filteredProductElements.length;
            this.state.totalPages = Math.ceil(this.state.totalResults / this.state.resultsPerPage);
            this.updateCategoryFacetCounts();
        } else {
            const productResults = await this.getProducts();
            this.state.productList = productResults.dataList;
            this.state.totalResults = productResults.totalNumber;
            this.state.totalPages = Math.ceil(this.state.totalResults / this.state.resultsPerPage);
            this.updateFacetCounts(productResults.facets);
            this.elements.productGrid.querySelectorAll(`.${componentName}__product`).forEach((product) => {
                if (!product.classList.contains(`${componentName}__product-template`)) {
                    product.remove();
                }
            });
            this.state.productList.forEach((product) => {
                this.elements.productGrid.appendChild(this.createProductElement(product));
            });
        }
        this.elements.paginationForm.querySelector(`.${componentName}__pagination-input`).value = 1;
        [...this.element.querySelectorAll(`.${componentName}__pagination-start-index`)].forEach((startIndex) => {
            startIndex.textContent = 1;
        });
        [...this.element.querySelectorAll(`.${componentName}__pagination-end-index`)].forEach((endIndex) => {
            endIndex.textContent = Math.min(this.state.resultsPerPage, this.state.totalResults);
        });
        [...this.element.querySelectorAll(`.${componentName}__pagination-total-count`)].forEach((totalCount) => {
            totalCount.textContent = this.state.totalResults;
        });
        this.elements.paginationForm.querySelector('span[data-name="page-count"]').textContent = this.state.totalPages;
        this.hideSpinner();
        if (this.state.pageIndex < this.state.totalPages - 1) {
            this.elements.paginationForm.querySelector(`.${componentName}__pagination-button[name="next"]`).removeAttribute('disabled');
        }
        this.elements.paginationForm.querySelector(`.${componentName}__pagination-input`).removeAttribute('disabled');
        this.elements.paginationForm.querySelector(`.${componentName}__pagination-input`).setAttribute('max', this.state.totalPages);
    };

    handleProductFilterChange = async () => {
        this.showSpinner();

        const filterFormData = new FormData(this.elements.filterForm);
        this.state.filters = {};
        for (let [key, value] of filterFormData) {
            if (value !== '' && key !== 'mobileSort' && key !== 'textSearch') {
                if (key.startsWith('range-input-')) {
                    // const rangeInput = this.elements.filterForm.querySelector(`input[name="${key}"]`).closest(`.${componentName}__filter-slider-container`);
                    // if (this.rangeInputEmpty(rangeInput)) {
                    //     continue;
                    // }
                    // key = key.split('-').slice(0, key.split('-').length - 1).join('-');
                }
                if (this.state.filters[key]) {
                    this.state.filters[key].push(value);
                } else {
                    this.state.filters[key] = [value];
                }
            }
        }

        this.elements.filterClearAll.hidden = (this.elements.filterForm.querySelectorAll('input[type="checkbox"]:checked').length === 0 && (this.state.textSearch === ''));
        this.elements.paginationForm.querySelector(`.${componentName}__pagination-button[name="prev"]`).setAttribute('disabled', true);
        this.elements.paginationForm.querySelector(`.${componentName}__pagination-button[name="next"]`).setAttribute('disabled', true);
        this.elements.paginationForm.querySelector(`.${componentName}__pagination-input`).setAttribute('disabled', true);
        this.state.pageIndex = 0;

        if (this.constants.listType === 'category') {
            const filteredProductElements = [];
            this.elements.subcategoryGrid.querySelectorAll(`.${componentName}__product`).forEach((product) => {
                let productFilteredOut = false;
                const isProductDiscontinued = product.classList.contains('discontinued');
                if (isProductDiscontinued && (!this.state.filters['discontinued'] || !this.state.filters['discontinued'].includes('true'))) {
                    productFilteredOut = true;
                }
                Object.keys(this.state.filters).forEach((filter) => {
                    if (filter === 'discontinued') {
                        return;
                    }
                    const metadataElement = product.querySelector(`.${componentName}__product-filter[data-name="${filter}"]`);
                    let filterValueFound = false;
                    this.state.filters[filter].forEach((filterValue) => {
                        if (metadataElement && metadataElement.querySelector(`.${componentName}__product-filter-values[data-value="${filterValue}"]`)) {
                            filterValueFound = true;
                        }
                    });
                    if (!filterValueFound) {
                        productFilteredOut = true;
                    }
                });
                if (!productFilteredOut) {
                    filteredProductElements.push(product);
                }
            });

            const products = this.elements.subcategoryGrid.querySelectorAll(`.${componentName}__product`);
            let index = 0;
            for (let i = 0; i < products.length; i++) {
                const product = products[i];
                product.classList.add('filtered-out');
                if (filteredProductElements.includes(product)) {
                    product.classList.remove('filtered-out');
                } else {
                    continue;
                }
                if (index >= this.state.resultsPerPage) {
                    product.classList.add('hidden');
                } else {
                    product.classList.remove('hidden');
                }
                index++;
            }
            this.state.totalResults = filteredProductElements.length;
            this.updateCategoryFacetCounts();
        } else {
            const productResults = await this.getProducts();
            this.updateFacetCounts(productResults.facets);
            this.state.productList = productResults.dataList;
            this.state.totalResults = productResults.totalNumber;
            this.elements.productGrid.querySelectorAll(`.${componentName}__product`).forEach((product) => {
                if (!product.classList.contains(`${componentName}__product-template`)) {
                    product.remove();
                }
            });
            this.state.productList.forEach((product) => {
                this.elements.productGrid.appendChild(this.createProductElement(product));
            });
        }

        this.state.totalPages = Math.ceil(this.state.totalResults / this.state.resultsPerPage);
        this.elements.paginationForm.querySelector(`.${componentName}__pagination-input`).value = 1;
        [...this.element.querySelectorAll(`.${componentName}__pagination-start-index`)].forEach((startIndex) => {
            startIndex.textContent = 1;
        });
        [...this.element.querySelectorAll(`.${componentName}__pagination-end-index`)].forEach((endIndex) => {
            endIndex.textContent = Math.min(this.state.resultsPerPage, this.state.totalResults);
        });
        [...this.element.querySelectorAll(`.${componentName}__pagination-total-count`)].forEach((totalCount) => {
            totalCount.textContent = this.state.totalResults;
        });
        this.elements.paginationForm.querySelector('span[data-name="page-count"]').textContent = this.state.totalPages;

        this.hideSpinner();
        if (this.state.pageIndex < this.state.totalPages - 1) {
            this.elements.paginationForm.querySelector(`.${componentName}__pagination-button[name="next"]`).removeAttribute('disabled');
        }
        this.elements.paginationForm.querySelector(`.${componentName}__pagination-input`).removeAttribute('disabled');
        this.elements.paginationForm.querySelector(`.${componentName}__pagination-input`).setAttribute('max', this.state.totalPages);

        this.rerenderFacetBuckets();
    };

    rerenderFacetBuckets = () => {
        this.elements.filterContainer.querySelectorAll(`.${componentName}__filter-group`).forEach(group => {
            const list = group.querySelector(".product-family-list__filter-content");
            this.reorderSelectedToTop(list);
        })
    }

    // Reorder function for this list
    reorderSelectedToTop = (list) => {
        const items = Array.from(list.children);
        items.sort((a, b) => {
            const aChecked = a.querySelector('input[type="checkbox"]')?.checked;
            const bChecked = b.querySelector('input[type="checkbox"]')?.checked;

            // Selected first
            if (aChecked && !bChecked) return -1;
            if (!aChecked && bChecked) return 1;

            // If both are the same (selected or unselected), preserve original order
            // console.log(a.dataset.originalIndex);
            return Number(a.dataset.originalIndex) - Number(b.dataset.originalIndex);
        });

        // Move elements in new order
        return items.forEach((item) => list.appendChild(item));
    };

    createFilterChip = (filterCategory, filterValue, filterLabel) => {
        const chipContainer = this.elements.chipContainer;
        const chipTemplate = chipContainer.querySelector(`.${componentName}__chip-template`);
        const chip = chipTemplate.cloneNode(true);
        chip.classList.remove(`${componentName}__chip-template`);
        chip.querySelector(`.${componentName}__chip-text`).textContent = filterLabel;
        chip.setAttribute('data-name', filterCategory);
        chip.setAttribute('data-value', filterValue);
        chip.addEventListener('click', () => this.handleFilterChipClick(chip));
        chipContainer.appendChild(chip);
    };

    handleFilterChipClick = (chip) => {
        const filterCategory = chip.dataset.name;
        const filterValue = chip.dataset.value;
        if (filterCategory === 'textSearch') {
            this.elements.textSearchInput.value = '';
            this.state.textSearch = '';
            this.handleTextSearchChange();
        } else {
            const checkbox = this.elements.filterForm.querySelector(`input[name="${filterCategory}"][value="${filterValue}"]`);
            checkbox.click();
        }
        chip.remove();
    };

    // rangeInputsEmpty = () => {
    //     let rangeInputsEmpty = true;
    //     const rangeInputs = this.elements.filterForm.querySelectorAll(`.${componentName}__filter-slider-container`);
    //     rangeInputs.forEach((rangeInput) => {
    //         if (!rangeInput.classList.contains(`${componentName}__filter-slider-container-template`)) {
    //             rangeInputsEmpty = this.rangeInputEmpty(rangeInput) && rangeInputsEmpty;
    //         }
    //     });
    //     return rangeInputsEmpty;
    // };

    // rangeInputEmpty = (rangeInput) => {
    //     const rangeInput1 = rangeInput.querySelector(`.${componentName}__filter-slider-range[data-index="0"]`);
    //     const rangeInput2 = rangeInput.querySelector(`.${componentName}__filter-slider-range[data-index="1"]`);
    //     if ((rangeInput1.value !== rangeInput1.min && rangeInput1.value != rangeInput1.max) || (rangeInput2.value != rangeInput2.min && rangeInput2.value != rangeInput2.max) || (rangeInput1.value == rangeInput2.value)) {
    //         return false;
    //     }
    //     return true;
    // };

    toTitle = (label) => {
        // convert all caps to title case
        label = label.replace(/([A-Z]{2,})/g, (str) => str.toLowerCase());
        // convert to title case
        label = label.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());
        return label;
    };

    compareCheckboxClick = async (event) => {
        const compareButtons = document.querySelectorAll(`.${componentName}__product-compare-checkbox`);
        const compareButton = event.target;
        const catalogNumber = compareButton.dataset.sku;
        if (compareButton.checked) {
            compareButton.setAttribute('data-var', 'checked');
            if (!this.state.userCompareData.includes(catalogNumber) && this.state.userCompareData.length < 4) {
                this.state.userCompareData.push(catalogNumber);
                window.localStorage.setItem('userCompareData', this.state.userCompareData.join(', '));
                if (this.state.userCompareData.length === 4) {
                    compareButtons.forEach((button) => {
                        if (!this.state.userCompareData.includes(button.dataset.sku)) {
                            button.disabled = true;
                        }
                    });
                }
            }
        } else {
            compareButton.setAttribute('data-var', 'unchecked');
            let index = 0;
            this.elements.compareItems.forEach((item, itemIndex) => {
                if (item.dataset.sku === catalogNumber) {
                    index = itemIndex;
                }
            });
            this.removeCompareFrame(index, catalogNumber);
            compareButtons.forEach((button) => {
                button.disabled = false;
            });
        }
        await this.showCompareModal();
    }

    showCompareModal = async () => {
        const compareModal = document.querySelector(`.${componentName}__compare-modal`);
        const compareModalCounts = document.querySelectorAll(`.${componentName}__compare-modal-count-number`);
        compareModalCounts.forEach((count) => {
            count.innerText = count.classList.contains(`${componentName}__compare-modal-count-number--mobile`) ? `(${this.state.userCompareData.length})` : `${this.state.userCompareData.length}`;
        });
        if (this.state.userCompareData.length > 0) {
            const jsonData = await this.getCompareData(this.state.userCompareData);
            // Check if jsonData is array or object
            if (jsonData.length) {
                jsonData.sort(this.sortCompareData)
                jsonData.forEach((element, index) => {
                    this.createCompareFrame(element, index);
                });
            } else {
                this.createCompareFrame(jsonData, 0);
            }
            compareModal.classList.add('active');
        }
    }

    sortCompareData = (productA, productB) => {
        return this.state.userCompareData.indexOf(productA.catalogNumber) - this.state.userCompareData.indexOf(productB.catalogNumber);
    }

    removeCompareFrame = (index, catalogNumber) => {
        this.state.userCompareData = this.state.userCompareData.filter((item) => item !== catalogNumber);
        window.localStorage.setItem('userCompareData', this.state.userCompareData.join(', '));
        const compareModalCounts = document.querySelectorAll(`.${componentName}__compare-modal-count-number`);
        compareModalCounts.forEach((count) => {
            count.innerText = count.classList.contains(`${componentName}__compare-modal-count-number--mobile`) ? `(${this.state.userCompareData.length})` : `${this.state.userCompareData.length}`;
        });
        this.updateCompareThumbnail(index, '');
        if (this.state.userCompareData.length === 0) {
            const compareModal = document.querySelector(`.${componentName}__compare-modal`);
            compareModal.classList.remove('active');
        }
    }

    removeAllCompareFrames = () => {
        const compareModalItems = document.querySelectorAll(`.${componentName}__compare-modal-item`);
        for (let i = 0; i < 4; i++) {
            compareModalItems[i].dataset.sku = '';
            compareModalItems[i].querySelector(`.${componentName}__compare-modal-item-image img`).src = '';
            compareModalItems[i].querySelector(`.${componentName}__compare-modal-item-image img`).alt = '';
            compareModalItems[i].querySelector(`.${componentName}__compare-modal-item-image`).classList.remove('active');
            compareModalItems[i].querySelector(`.${componentName}__compare-modal-item-remove`).classList.remove('active');
        }
        const compareModalCounts = document.querySelectorAll(`.${componentName}__compare-modal-count-number`);
        compareModalCounts.forEach((count) => {
            count.innerText = count.classList.contains(`${componentName}__compare-modal-count-number--mobile`) ? `(${this.state.userCompareData.length})` : `${this.state.userCompareData.length}`;
        });
        const compareModal = document.querySelector(`.${componentName}__compare-modal`);
        compareModal.classList.remove('active');
    }

    createCompareFrame = (jsonData, index) => {
        const catalogNumber = (jsonData.catalogNumber && jsonData.catalogNumber != 'null') ? jsonData.catalogNumber : 'N/A';
        const productImage = (jsonData.picture_url && jsonData.picture_url != 'null') ? jsonData.picture_url : this.getDefaultImage();
        this.updateCompareThumbnail(index, catalogNumber, productImage);
    }

    getCompareData = async (catalogNumbers) => {
        if (!catalogNumbers.length) {
            return [];
        }
        try {
            const locale = document.documentElement.lang ? document.documentElement.lang.toLocaleLowerCase() : 'en-us';
            const countryCode = locale.split('-')[1];
            const response = await fetch(`${productDetailServletEndpoint}?catalogNumber=${catalogNumbers.join(',')}&app=${this.constants.app}&countryCode=${countryCode}`);
            const jsonData = await response.json();
            return jsonData;
        } catch (error) {
            console.warn('Error fetching compare data', error);
            return [];
        }
    }

    updateCompareThumbnail = (index, catalogNumber, image) => {
        const compareModalItems = document.querySelectorAll(`.${componentName}__compare-modal-item`);
        if (image) {
            compareModalItems[index].dataset.sku = catalogNumber;
            compareModalItems[index].querySelector(`.${componentName}__compare-modal-item-image img`).src = image;
            compareModalItems[index].querySelector(`.${componentName}__compare-modal-item-image img`).alt = catalogNumber;
            compareModalItems[index].querySelector(`.${componentName}__compare-modal-item-image`).classList.add('active');
            compareModalItems[index].querySelector(`.${componentName}__compare-modal-item-remove`).classList.add('active');
        } else {
            compareModalItems[index].dataset.sku = '';
            compareModalItems[index].querySelector(`.${componentName}__compare-modal-item-image img`).src = '';
            compareModalItems[index].querySelector(`.${componentName}__compare-modal-item-image img`).alt = '';
            compareModalItems[index].querySelector(`.${componentName}__compare-modal-item-image`).classList.remove('active');
            compareModalItems[index].querySelector(`.${componentName}__compare-modal-item-remove`).classList.remove('active');
            if (index < 3 && [...this.elements.compareItems][index + 1].dataset.sku) {
                this.updateCompareThumbnail(index, [...this.elements.compareItems][index + 1].dataset.sku, compareModalItems[(index + 1)].querySelector(`.${componentName}__compare-modal-item-image img`).src);
                this.updateCompareThumbnail((index + 1), '');
            }
        }
    }

    refreshCompareData = async () => {
        let updatedCompareData = [];
        if (window.localStorage.getItem('userCompareData') === null) {
            updatedCompareData = this.state.userCompareData;
        } else if (window.localStorage.getItem('userCompareData').trim() === '') {
            updatedCompareData = [];
        } else {
            updatedCompareData = window.localStorage.getItem('userCompareData').split(', ');
        }
        // If data is not updated, return
        if (updatedCompareData.length === this.state.userCompareData.length && updatedCompareData.every((value) => value === this.state.userCompareData.includes(value))) {
            return;
        }
        this.state.userCompareData = updatedCompareData;
        this.removeAllCompareFrames();
        document.querySelectorAll(`.${componentName}__product-compare-checkbox`).forEach((checkbox) => {
            if (this.state.userCompareData.includes(checkbox.dataset.sku)) {
                checkbox.checked = true;
            } else {
                checkbox.checked = false;
            }
            if (this.state.userCompareData.length === 4 && !this.state.userCompareData.includes(checkbox.dataset.sku)) {
                checkbox.disabled = true;
            } else {
                checkbox.disabled = false;
            }
        });
        if (this.state.userCompareData.length > 0) {
            await this.showCompareModal();
        }
    }

    updateCategoryFacetCounts = () => {
        this.elements.filterCheckboxes.forEach((checkbox) => {
            const countElement = checkbox.closest(`.${componentName}__filter-checkbox-container`).querySelector(`.${componentName}__filter-checkbox-count`);
            let checkboxCount = 0;
            if ((checkbox.closest(`.${componentName}__filter-content`) && checkbox.closest(`.${componentName}__filter-content`).querySelector(`.${componentName}__filter-checkbox:checked`)) || checkbox.name === 'discontinued') {
                checkboxCount = this.elements.subcategoryGrid.querySelectorAll(`.${componentName}__product-filter[data-name="${checkbox.name}"] .${componentName}__product-filter-values[data-value="${checkbox.value}"]`).length;
                countElement.textContent = `(${checkboxCount})`;
            } else {
                checkboxCount = this.elements.subcategoryGrid.querySelectorAll(`.${componentName}__product:not(.filtered-out) .${componentName}__product-filter[data-name="${checkbox.name}"] .${componentName}__product-filter-values[data-value="${checkbox.value}"]`).length;
                countElement.textContent = `(${checkboxCount})`;
            }
            if (checkboxCount > 0) {
                checkbox.disabled = false;
            } else {
                checkbox.disabled = true;
            }
        });
    }

    slideUp = (element, duration = 300) => {
        return new Promise((resolve) => {
            element.style.height = element.offsetHeight + 'px';
            element.style.transitionProperty = 'height, margin, padding';
            element.style.transitionDuration = duration + 'ms';
            element.offsetHeight; // trigger reflow
            element.style.overflow = 'hidden';
            element.style.height = 0;
            element.style.paddingTop = 0;
            element.style.paddingBottom = 0;
            element.style.marginTop = 0;
            element.style.marginBottom = 0;
            setTimeout(() => {
                element.style.display = 'none';
                element.style.removeProperty('height');
                element.style.removeProperty('padding-top');
                element.style.removeProperty('padding-bottom');
                element.style.removeProperty('margin-top');
                element.style.removeProperty('margin-bottom');
                element.style.removeProperty('overflow');
                element.style.removeProperty('transition-duration');
                element.style.removeProperty('transition-property');
                resolve();
            }, duration);
        });
    };

    slideDown = (element, duration = 300) => {
        return new Promise((resolve) => {
            element.style.removeProperty('display');
            let display = window.getComputedStyle(element).display;
            if (display === 'none') display = 'block';
            element.style.display = display;
            let height = element.offsetHeight;
            element.style.overflow = 'hidden';
            element.style.height = 0;
            element.style.paddingTop = 0;
            element.style.paddingBottom = 0;
            element.style.marginTop = 0;
            element.style.marginBottom = 0;
            element.offsetHeight; // trigger reflow
            element.style.transitionProperty = 'height, margin, padding';
            element.style.transitionDuration = duration + 'ms';
            element.style.height = height + 'px';
            element.style.removeProperty('padding-top');
            element.style.removeProperty('padding-bottom');
            element.style.removeProperty('margin-top');
            element.style.removeProperty('margin-bottom');
            setTimeout(() => {
                element.style.removeProperty('height');
                element.style.removeProperty('overflow');
                element.style.removeProperty('transition-duration');
                element.style.removeProperty('transition-property');
                resolve();
            }, duration);
        });
    };

    /**
     * Adds slide up/down animation when details element is opened/closed
     * @param {MouseEvent} ev
     */
    handleSummaryClick = (ev) => {
        ev.preventDefault();
        const detailsEl = ev.currentTarget.closest('details');
        const contentEl = detailsEl.querySelector('div');

        if (!detailsEl.open) {
            // Open
            contentEl.style.display = 'none';
            detailsEl.open = true;
            this.slideDown(contentEl);
        } else {
            // Close
            this.slideUp(contentEl).then(() => {
                detailsEl.open = false;
            });
        }
    };

    readUrlParams = () => {
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.forEach((value, key) => {
            if (key === 'keyword') {
                this.state.textSearch = value;
                if (this.elements.textSearchInput) {
                    this.elements.textSearchInput.value = value;
                    this.createFilterChip('textSearch', value, value);
                }
            } else {
                const filterCheckbox = this.elements.filterForm.querySelector(`.${componentName}__filter-checkbox[name="${key}"][value="${encodeURIComponent(value)}"]`);
                if (filterCheckbox) {
                    this.state.filters[key] = [value];
                    filterCheckbox.checked = true;
                    this.createFilterChip(key, encodeURIComponent(value), filterCheckbox.closest(`.${componentName}__filter-checkbox-container`).querySelector('label').textContent);
                }
            }
        });
    }

    encodeCatalogNumber = (catalogNumber) => {
        return catalogNumber.replaceAll('/', '%252F').replaceAll('.', '%252E');
    }

    addEventListeners = () => {
        if (this.constants.listType !== 'category') {
            const compareModalClose = document.querySelector(`.${componentName}__compare-modal-close`);
            compareModalClose.addEventListener('click', () => {
                const compareModal = document.querySelector(`.${componentName}__compare-modal`);
                compareModal.classList.remove('active');
            });
            compareModalClose.addEventListener('keydown', (event) => {
                if (event.key === 'Enter') {
                    compareModalClose.click();
                }
            });
            const compareModalRemoveButtons = document.querySelectorAll(`.${componentName}__compare-modal-item-remove`);
            compareModalRemoveButtons.forEach((removeButton, index) => {
                removeButton.addEventListener('click', () => {
                    const catalogNumber = this.elements.compareItems[index].dataset.sku;
                    this.removeCompareFrame(index, catalogNumber);
                    const compareButton = document.querySelector(`.${componentName}__product-compare-checkbox[data-sku="${catalogNumber}"]`);
                    if (compareButton) {
                        compareButton.checked = false;
                    }
                    const compareButtons = document.querySelectorAll(`.${componentName}__product-compare-checkbox`);
                    compareButtons.forEach((button) => {
                        button.disabled = false;
                    });
                });
                removeButton.addEventListener('keydown', (event) => {
                    if (event.key === 'Enter') {
                        removeButton.click();
                    }
                });
            });
            const compareModalRemoveAll = document.querySelector(`.${componentName}__compare-modal-remove-all`);
            compareModalRemoveAll.addEventListener('click', (event) => {
                this.state.userCompareData = [];
                window.localStorage.setItem('userCompareData', this.state.userCompareData.join(', '));
                this.removeAllCompareFrames();
                const compareButtons = document.querySelectorAll(`.${componentName}__product-compare-checkbox`);
                compareButtons.forEach((button) => {
                    button.checked = false;
                    button.disabled = false;
                });
            });
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'visible') {
                    this.refreshCompareData();
                }
            });
        }
        this.elements.filterForm.addEventListener('submit', (ev) => {
            ev.preventDefault();
        });
        this.elements.filterClearAll.addEventListener('click', async () => {
            this.elements.filterForm.reset();
            this.elements.chipContainer.querySelectorAll(`.${componentName}__chip`).forEach((chip) => {
                if (!chip.classList.contains(`${componentName}__chip-template`)) {
                    chip.remove();
                }
            });
            if (this.constants.listType !== 'search') {
                this.state.textSearch = '';
            }
            // this.elements.filterForm.querySelectorAll(`.${componentName}__filter-slider-container`).forEach((rangeInput) => {
            //     if (!rangeInput.classList.contains(`${componentName}__filter-slider-container-template`)) {
            //         const rangeInput1 = rangeInput.querySelector(`.${componentName}__filter-slider-range[data-index="0"]`);
            //         const rangeInput2 = rangeInput.querySelector(`.${componentName}__filter-slider-range[data-index="1"]`);
            //         rangeInput1.value = rangeInput1.min;
            //         rangeInput2.value = rangeInput2.max;
            //         this.fillSlider(rangeInput1, rangeInput2, '#949494', '#003E7E', rangeInput1);
            //     }
            // });
            await this.handleProductFilterChange();
            // [...this.elements.productSortInputsMobile].find((input) => input.value === this.state.sortBy).checked = true;
        });
        this.elements.filterClearAll.addEventListener('keydown', (ev) => {
            if (ev.key === 'Enter') {
                this.elements.filterClearAll.click();
            }
        });
        this.elements.filterCheckboxes.forEach((checkbox) => {
            checkbox.addEventListener('change', () => {
                if (checkbox.checked) {
                    this.createFilterChip(checkbox.name, checkbox.value, checkbox.closest(`.${componentName}__filter-checkbox-container`).querySelector('label').textContent);
                } else {
                    this.elements.chipContainer.querySelector(`[data-name="${checkbox.name}"][data-value="${checkbox.value}"]`).remove();
                }
                this.handleProductFilterChange();
            });
        });
        this.elements.paginationForm.addEventListener('submit', this.handlePaginationFormSubmit);
        this.elements.paginationForm.querySelector(`.${componentName}__pagination-button[name="prev"]`).addEventListener('click', () => {
            this.updatePageIndex(this.state.pageIndex - 1);
        });
        this.elements.paginationForm.querySelector(`.${componentName}__pagination-button[name="prev"]`).addEventListener('keydown', (ev) => {
            if (ev.key === 'Enter') {
                this.elements.paginationForm.querySelector(`.${componentName}__pagination-button[name="prev"]`).click();
            }
        });
        this.elements.paginationForm.querySelector(`.${componentName}__pagination-button[name="next"]`).addEventListener('click', () => {
            this.updatePageIndex(this.state.pageIndex + 1);
        });
        this.elements.paginationForm.querySelector(`.${componentName}__pagination-button[name="next"]`).addEventListener('keydown', (ev) => {
            if (ev.key === 'Enter') {
                this.elements.paginationForm.querySelector(`.${componentName}__pagination-button[name="next"]`).click();
            }
        });
        this.elements.paginationForm.querySelector(`.${componentName}__pagination-input`).addEventListener('change', (event) => {
            this.updatePageIndex(parseInt(event.target.value, 10) - 1);
        });
        this.elements.resultsPerPageSelect.addEventListener('change', this.handleResultsPerPageChange);
        // this.elements.productSortSelect.addEventListener('change', (ev) => {
        //     this.handleProductSortChange(ev.target.value);
        //     [...this.elements.productSortInputsMobile].find((input) => input.value === this.state.sortBy).checked = true;
        // });
        // [...this.elements.productSortInputsMobile].forEach((input) => {
        //     input.addEventListener('change', (ev) => {
        //         this.elements.productSortSelect.querySelector(`option[value="${input.value}"]`).selected = true;
        //         this.elements.productSortSelect.dispatchEvent(new Event('change'));
        //         const selectedOptionIndex = [...this.elements.productSortSelect.options].findIndex((option) => option.value === input.value);
        //         this.elements.productSortSelectStyler.querySelector('summary span').textContent = this.elements.productSortSelect.querySelector(`option[value="${input.value}"]`).textContent;
        //         this.elements.productSortSelectStyler.querySelectorAll('ul[role="listbox"] li').forEach((option, index) => {
        //             option.setAttribute('aria-selected', index === selectedOptionIndex);
        //         });
        //     });
        // });
        this.elements.mobileFilterOpenButton.addEventListener('click', () => {
            this.elements.filterContainer.classList.add('active');
            document.body.classList.add('no-scroll');
        });
        this.elements.mobileFilterOpenButton.addEventListener('keydown', (ev) => {
            if (ev.key === 'Enter') {
                this.elements.mobileFilterOpenButton.click();
            }
        });
        [...this.elements.mobileFilterCloseButtons].forEach((button) => {
            button.addEventListener('click', () => {
                this.elements.filterContainer.classList.remove('active');
                document.body.classList.remove('no-scroll');
            });
            button.addEventListener('keydown', (ev) => {
                if (ev.key === 'Enter') {
                    button.click();
                }
            });
        });

        if (this.elements.textSearchInput) {
            this.elements.textSearchInput.addEventListener('keydown', (ev) => {
                this.state.textSearch = ev.target.value.trim();
                if (ev.key === 'Enter') {
                    if (this.state.textSearch !== '' && !this.elements.chipContainer.querySelector(`[data-name="textSearch"]`)) {
                        this.createFilterChip('textSearch', this.state.textSearch, this.state.textSearch);
                    } else if (this.state.textSearch !== '' && this.elements.chipContainer.querySelector(`[data-name="textSearch"]`)) {
                        this.elements.chipContainer.querySelector(`[data-name="textSearch"]`).querySelector(`.${componentName}__chip-text`).textContent = this.state.textSearch;
                    } else if (this.state.textSearch === '' && this.elements.chipContainer.querySelector(`[data-name="textSearch"]`)) {
                        this.elements.chipContainer.querySelector(`[data-name="textSearch"]`).remove();
                    }
                    this.handleTextSearchChange();
                }
            });
        }

        this.elements.compareItems.forEach((item) => {
            item.querySelector('img').addEventListener('error', (ev) => {
                if (ev.target.src === this.getDefaultImage()) {
                    return;
                }
                ev.target.src = this.getDefaultImage();
            });
        });

        this.elements.filterForm.querySelectorAll(`.${componentName}__filter-summary`).forEach((summary) => {
            summary.addEventListener('click', this.handleSummaryClick);
            summary.addEventListener('keydown', (ev) => {
                if (ev.key === 'Enter') {
                    this.handleSummaryClick(ev);
                }
            });
        });
    };

    initDom = async () => {
        this.constants.app = this.elements.container.getAttribute('data-app') ? this.elements.container.getAttribute('data-app') : 'RA';
        this.showSpinner();
        this.constants.listType = this.elements.container.getAttribute('data-list-type');
        this.constants.categoryIds = this.elements.container.getAttribute('data-category-list');
        if (this.constants.listType === 'category') {
            this.state.totalResults = this.elements.subcategoryGrid.children.length;
            this.state.totalPages = Math.ceil(this.state.totalResults / this.state.resultsPerPage);
            this.elements.filterCheckboxes = this.element.querySelectorAll(`.${componentName}__filter-checkbox`);
            // this.handleProductSortChange(this.elements.productSortSelect.value);
            this.readUrlParams();
            this.handleProductFilterChange();
            this.updateCategoryFacetCounts();
        } else {
            const productResults = await this.getProducts();
            this.state.totalResults = productResults.totalNumber;
            this.state.totalPages = Math.ceil(this.state.totalResults / this.state.resultsPerPage);
            this.initFacets(productResults.facets);
            this.addFacets();
            this.readUrlParams();
            this.handleProductFilterChange();
            this.refreshCompareData();
        }
        this.elements.paginationForm.querySelector(`.${componentName}__pagination-input`).setAttribute('max', this.state.totalPages);
        this.elements.paginationForm.querySelector(`.${componentName}__pagination-input`).setAttribute('value', this.state.pageIndex + 1);
        [...this.element.querySelectorAll(`.${componentName}__pagination-end-index`)].forEach((endIndex) => {
            endIndex.textContent = Math.min(this.state.resultsPerPage, this.state.totalResults);
        });
        [...this.element.querySelectorAll(`.${componentName}__pagination-total-count`)].forEach((totalCount) => {
            totalCount.textContent = this.state.totalResults;
        });
        this.elements.paginationForm.querySelector('span[data-name="page-count"]').textContent = this.state.totalPages;
        if (!(this.state.totalPages === 1)) {
            this.elements.paginationForm.querySelector(`.${componentName}__pagination-input`).removeAttribute('disabled');
            this.elements.paginationForm.querySelector(`.${componentName}__pagination-button[name="next"]`).removeAttribute('disabled');
        }
        this.hideSpinner();
    };

    /**
     * Initialize Component
     */
    init = async () => {
        await this.initDom();
        this.addEventListeners();
    };
}

registerComponent(`.${componentName}`, ProductFamilyList);
